import dayjs from 'dayjs';
import { Activity, useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { API_KEY_CONST } from '@/constant';
import {
  useAPIKeyCreateMutation,
  useAPIKeyUpdateMutation,
} from '@/hook/apiKey';
import {
  ContentCopy,
  VButton,
  VDatePicker,
  VFlex,
  VInput,
  VStack,
  VToggleButton,
  formatDate,
  useVModal,
  useVToast,
} from '@/package';
import type { APIKey } from '@/type';
import { ERROR_CODE } from '@/util';

type APIKeyFormData = Pick<
  APIKey,
  'apiKey' | 'createdAt' | 'expireAt' | 'id' | 'name' | 'status'
>;

type APIKeyFormProps = {
  formDefaultValue: APIKeyFormData & Pick<APIKey, 'createdAt'>;
  isEditablePermission?: boolean;
  onSubmitted?(): void;
};

const UNLIMITED_EXPIRE_AT = dayjs('9999-12-31').toISOString();

export const APIKeyForm = ({
  formDefaultValue,
  isEditablePermission,
  onSubmitted,
}: APIKeyFormProps) => {
  const { t } = useTranslation();
  const { addToast } = useVToast();
  const { openDangerAlert } = useVModal();
  const { formState, handleSubmit, register, setValue, watch } =
    useForm<APIKeyFormData>({
      defaultValues: {
        apiKey: '',
        expireAt: formDefaultValue.expireAt,
        name: formDefaultValue.name,
        status: formDefaultValue.status ?? API_KEY_CONST.API_KEY_STATUS.ENABLE,
      },
    });
  const { mutateAsync: create } = useAPIKeyCreateMutation();
  const { mutateAsync: update } = useAPIKeyUpdateMutation();
  const editable = !!formDefaultValue.id;
  // eslint-disable-next-line react-hooks/incompatible-library
  const unlimitedExpireAt = watch('expireAt') === UNLIMITED_EXPIRE_AT;

  const onSubmit: SubmitHandler<APIKeyFormData> = formData => {
    const mutatedResult = !editable
      ? create(formData)
      : update({
          id: formDefaultValue.id,
          name: formData.name,
          status: formData.status,
        });

    mutatedResult
      .then(() => onSubmitted?.())
      .catch(err => {
        const { resultCode } = err.data;
        let message: string = t('error.defaultError.coperiodic');

        switch (resultCode as string) {
          case ERROR_CODE[1400]:
            message = t('apiKey.createError1400Message.overshroud');
            break;
          case ERROR_CODE[45423]:
            message = t('apiKey.createError45423Message.rebunker', {
              limit: JSON.parse(err.data.resultMsg)?.limit,
            });
            break;
        }

        openDangerAlert({
          message,
          size: 'small',
          title: t('common.errorTitle.preconfirmation'),
        });
      });
  };

  useEffect(() => {
    setValue(
      'apiKey',
      formDefaultValue.id
        ? `${formDefaultValue.apiKey.substring(0, 6)}************`
        : formDefaultValue.apiKey,
    );
  }, [formDefaultValue.apiKey]);

  useEffect(() => {
    register('expireAt', {
      validate: value =>
        dayjs(value).diff(dayjs()) <= 0
          ? t('apiKey.createModalFormExpireAtInvalid.housebreak')
          : true,
    });
  }, []);

  return (
    <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
      <VStack className="w-full" gap={1.5}>
        <VStack gap={0.5}>
          <VStack gap={0.5}>
            <div className="text-body-md">
              <strong>{t('apiKey.createModalFormTitle.airsick')}</strong>
            </div>

            <VStack gap={0.25}>
              <VFlex gap={0.5}>
                <VInput
                  {...register('name', {
                    required: {
                      message: t(
                        'apiKey.createModalFormKeyNameRequired.deadishness',
                      ),
                      value: true,
                    },
                  })}
                  autoFocus
                  className="flex-1"
                  comment={formState.errors.name?.message}
                  label={t('apiKey.tableColumnKeyName.greenalite')}
                  required
                  readOnly={!isEditablePermission}
                  tabIndex={0}
                  valid={formState.errors.name == null}
                />
                <div className="flex-1">
                  <VDatePicker
                    comment={formState.errors.expireAt?.message}
                    disabled={unlimitedExpireAt}
                    fluid
                    label={t('apiKey.tableColumnExpirationTime.samarium')}
                    readOnly={editable}
                    tabIndex={1}
                    valid={formState.errors.expireAt == null}
                    value={watch('expireAt')}
                    onChange={value => {
                      setValue('expireAt', dayjs(value as Date).toISOString(), {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    }}
                  />
                </div>
              </VFlex>

              <VFlex justify="end">
                <VToggleButton
                  isSelected={unlimitedExpireAt}
                  disabled={editable}
                  size="sm"
                  onClick={() => {
                    setValue(
                      'expireAt',
                      unlimitedExpireAt
                        ? dayjs().add(30, 'days').toISOString()
                        : UNLIMITED_EXPIRE_AT,
                    );
                  }}
                >
                  {t(
                    'apiKey.createModalNoExpirationDateButtonText.advertising',
                  )}
                </VToggleButton>
              </VFlex>
            </VStack>
          </VStack>

          <VStack gap={0.5}>
            <div className="text-body-md">
              <strong>{t('apiKey.tableColumnSecretKey.ritalynne')}</strong>
            </div>

            <VInput
              {...register('apiKey')}
              label={t('apiKey.tableColumnSecretKey.ritalynne')}
              orientation="horizontal"
              readOnly
              renderRightSide={() =>
                formDefaultValue.apiKey ? (
                  <ContentCopy
                    className="size-4 text-primary-500 hover:text-primary-400 active:text-primary-600"
                    role="button"
                    onClick={() => {
                      navigator.clipboard.writeText(formDefaultValue.apiKey);
                      addToast({
                        message: t('apiKey.secretKeyCopiedMessage.kolokolo'),
                        variant: 'info',
                        autoClose: true,
                        duration: 1000,
                      });
                    }}
                  />
                ) : null
              }
            />
          </VStack>
        </VStack>

        <VFlex justify="between" gap={0.5}>
          <Activity mode={editable ? 'visible' : 'hidden'}>
            <div className="text-body-md text-gray-500">
              {t('apiKey.tableColumnCreatedTime.seamlet')}&nbsp;
              {formatDate(formDefaultValue.createdAt, 'YYYY-MM-DD HH:mm:ss')}
            </div>

            {isEditablePermission && (
              <VButton outlined size="md" variant="secondary">
                {t('frame.doneState.hypoendocrinism')}
              </VButton>
            )}
            {!isEditablePermission && (
              <VButton
                outlined
                size="md"
                type="button"
                variant="secondary"
                onClick={onSubmitted}
              >
                {t('frame.doneState.hypoendocrinism')}
              </VButton>
            )}
          </Activity>
          <Activity mode={editable ? 'hidden' : 'visible'}>
            <div className="flex-1"></div>
            <VButton
              outlined
              size="md"
              type="reset"
              variant="secondary"
              onClick={onSubmitted}
            >
              {t('modal.cancelButtonText.intimidate')}
            </VButton>
            <VButton disabled={!formState.isValid} size="md">
              {t('common.save.semicivilization')}
            </VButton>
          </Activity>
        </VFlex>
      </VStack>
    </form>
  );
};
